import { AITool } from '../types'
import { visualDemoTools } from './visualDemoTools'
import { buySellTools } from './buySellTools'
import { watchlistTools } from './watchlistTools'

/**
 * Predefined trading tools for AI Terminal
 */

// Old placeOrderTool replaced by buySellTools.realPlaceOrderTool for visual integration
// export const placeOrderTool: AITool = { ... }

export const getPortfolioTool: AITool = {
  name: 'get_portfolio',
  description: 'Get current portfolio holdings and their values',
  category: 'portfolio',
  parameters: [],
  examples: [
    'Show my current portfolio',
    'What are my holdings?',
    'How much is my portfolio worth?'
  ],
  execute: async (params, context) => {
    // This would fetch real portfolio data
    return {
      totalValue: 10000,
      totalPnl: 500,
      totalPnlPercent: 5.0,
      holdings: [
        {
          symbol: 'BTC',
          quantity: 0.25,
          averagePrice: 45000,
          currentPrice: 47000,
          value: 11750,
          pnl: 500,
          pnlPercent: 4.44,
          allocation: 75.2
        },
        {
          symbol: 'ETH',
          quantity: 2.5,
          averagePrice: 3000,
          currentPrice: 3100,
          value: 7750,
          pnl: 250,
          pnlPercent: 3.33,
          allocation: 24.8
        }
      ]
    }
  }
}

export const getWatchlistTool: AITool = {
  name: 'get_watchlist',
  description: 'Get current watchlist with real-time prices',
  category: 'market_data',
  parameters: [],
  examples: [
    'Show my watchlist',
    'What stocks am I watching?',
    'Get prices for my watchlist'
  ],
  execute: async (params, context) => {
    // This would fetch real watchlist data
    return [
      {
        symbol: 'BTCUSDT',
        price: 47000,
        priceChange: 1000,
        priceChangePercent: 2.17,
        volume: 25000,
        isFavorite: true
      },
      {
        symbol: 'ETHUSDT',
        price: 3100,
        priceChange: 50,
        priceChangePercent: 1.64,
        volume: 15000,
        isFavorite: true
      }
    ]
  }
}

export const getTechnicalAnalysisTool: AITool = {
  name: 'get_technical_analysis',
  description: 'Get technical analysis indicators for a trading pair',
  category: 'analysis',
  parameters: [
    {
      name: 'symbol',
      type: 'string',
      description: 'Trading pair symbol',
      required: true
    },
    {
      name: 'timeframe',
      type: 'string',
      description: 'Chart timeframe (1m, 5m, 15m, 1h, 4h, 1d)',
      required: false,
      default: '1h'
    }
  ],
  examples: [
    'Get RSI for BTCUSDT',
    'Show MACD for ETH on 4h chart',
    'Technical analysis for SOLUSDT'
  ],
  execute: async (params, context) => {
    const { symbol, timeframe = '1h' } = params
    
    // This would calculate real technical indicators
    return {
      symbol,
      timeframe,
      rsi: 65.5,
      macd: {
        macd: 123.45,
        signal: 119.32,
        histogram: 4.13
      },
      sma: {
        '20': 46500,
        '50': 45800,
        '200': 44200
      },
      ema: {
        '12': 46800,
        '26': 46200
      },
      bollinger: {
        upper: 48500,
        middle: 47000,
        lower: 45500
      },
      support: [45000, 44000],
      resistance: [48000, 49000],
      trend: 'BULLISH' as const
    }
  }
}

export const addToWatchlistTool: AITool = {
  name: 'add_to_watchlist',
  description: 'Add a trading pair to the watchlist',
  category: 'watchlist',
  parameters: [
    {
      name: 'symbol',
      type: 'string',
      description: 'Trading pair symbol to add',
      required: true
    }
  ],
  examples: [
    'Add SOLUSDT to my watchlist',
    'Watch ADAUSDT',
    'Track LINKUSDT price'
  ],
  execute: async (params, context) => {
    const { symbol } = params
    
    // This would add to actual watchlist
    return {
      symbol,
      action: 'added',
      success: true,
      message: `${symbol} added to watchlist`
    }
  }
}

export const removeFromWatchlistTool: AITool = {
  name: 'remove_from_watchlist',
  description: 'Remove a trading pair from the watchlist',
  category: 'watchlist',
  parameters: [
    {
      name: 'symbol',
      type: 'string',
      description: 'Trading pair symbol to remove',
      required: true
    }
  ],
  examples: [
    'Remove BTCUSDT from watchlist',
    'Stop watching ETHUSDT',
    'Untrack SOLUSDT'
  ],
  execute: async (params, context) => {
    const { symbol } = params
    
    return {
      symbol,
      action: 'removed',
      success: true,
      message: `${symbol} removed from watchlist`
    }
  }
}

export const getMarketDataTool: AITool = {
  name: 'get_market_data',
  description: 'Get current market data for a trading pair',
  category: 'market_data',
  parameters: [
    {
      name: 'symbol',
      type: 'string',
      description: 'Trading pair symbol',
      required: true
    }
  ],
  examples: [
    'Get current price of BTCUSDT',
    'Show market data for ETHUSDT',
    'What is the volume for SOLUSDT?'
  ],
  execute: async (params, context) => {
    const { symbol } = params
    
    return {
      symbol,
      price: 47000,
      priceChange: 1000,
      priceChangePercent: 2.17,
      volume: 25000000,
      high24h: 48000,
      low24h: 45500,
      bid: 46999,
      ask: 47001,
      timestamp: new Date()
    }
  }
}

export const getOpenOrdersTool: AITool = {
  name: 'get_open_orders',
  description: 'Get all open/pending orders',
  category: 'trading',
  parameters: [
    {
      name: 'symbol',
      type: 'string',
      description: 'Filter by trading pair (optional)',
      required: false
    }
  ],
  examples: [
    'Show my open orders',
    'What orders do I have pending?',
    'Open orders for BTCUSDT'
  ],
  execute: async (params, context) => {
    return [
      {
        orderId: 'order_123',
        symbol: 'BTCUSDT',
        side: 'BUY',
        type: 'LIMIT',
        quantity: 0.1,
        price: 46000,
        status: 'open',
        timestamp: new Date()
      }
    ]
  }
}

export const cancelOrderTool: AITool = {
  name: 'cancel_order',
  description: 'Cancel an open order',
  category: 'trading',
  parameters: [
    {
      name: 'orderId',
      type: 'string',
      description: 'Order ID to cancel',
      required: true
    }
  ],
  examples: [
    'Cancel order order_123',
    'Remove my pending BTC order',
    'Cancel all open orders'
  ],
  execute: async (params, context) => {
    const { orderId } = params
    
    return {
      orderId,
      status: 'cancelled',
      message: `Order ${orderId} has been cancelled`
    }
  }
}

export const setSymbolTool: AITool = {
  name: 'set_symbol',
  description: 'Change the currently selected trading pair',
  category: 'navigation',
  parameters: [
    {
      name: 'symbol',
      type: 'string',
      description: 'Trading pair symbol to switch to',
      required: true
    }
  ],
  examples: [
    'Switch to BTCUSDT',
    'Go to ETHUSDT chart',
    'Change symbol to SOLUSDT'
  ],
  execute: async (params, context) => {
    const { symbol } = params
    
    // This would navigate to the symbol page
    window.location.href = `/${symbol.toLowerCase()}`
    
    return {
      symbol,
      success: true,
      message: `Switched to ${symbol}`
    }
  }
}

export const bulkBuyWatchlistTool: AITool = {
  name: 'bulk_buy_watchlist',
  description: 'Buy a specified quantity of each symbol in the watchlist',
  category: 'trading',
  parameters: [
    {
      name: 'quantity',
      type: 'number',
      description: 'Quantity to buy for each symbol',
      required: true
    },
    {
      name: 'orderType',
      type: 'string',
      description: 'Order type for all purchases',
      required: false,
      default: 'MARKET',
      enum: ['MARKET', 'LIMIT']
    }
  ],
  examples: [
    'Buy 10 shares of each symbol in my watchlist',
    'Purchase $100 worth of each watchlist item',
    'Market buy 5 units of all watched symbols'
  ],
  execute: async (params, context) => {
    const { quantity, orderType = 'MARKET' } = params
    
    // Get watchlist and place orders for each symbol
    const watchlist = await getWatchlistTool.execute({}, context)
    const orders = []
    
    for (const item of watchlist) {
      // Use realPlaceOrderTool from buySellTools instead
      const order = {
        orderId: `bulk_order_${Date.now()}_${item.symbol}`,
        symbol: item.symbol,
        side: 'BUY',
        type: orderType,
        quantity,
        status: 'submitted',
        timestamp: new Date()
      }
      orders.push(order)
    }
    
    return {
      totalOrders: orders.length,
      orders,
      message: `Placed ${orders.length} buy orders for watchlist symbols`
    }
  }
}

export const analyzePortfolioTool: AITool = {
  name: 'analyze_portfolio',
  description: 'Analyze current portfolio and provide insights',
  category: 'analysis',
  parameters: [],
  examples: [
    'Analyze my portfolio',
    'Portfolio performance analysis',
    'What should I rebalance?'
  ],
  execute: async (params, context) => {
    const portfolio = await getPortfolioTool.execute({}, context)
    
    // Perform portfolio analysis
    const analysis = {
      diversificationScore: 7.5,
      riskLevel: 'MODERATE',
      suggestions: [
        'Consider reducing BTC allocation from 75% to 60%',
        'Add more altcoins for diversification',
        'Your portfolio is overweight in large cap cryptocurrencies'
      ],
      rebalanceRecommendations: [
        { symbol: 'BTC', currentAllocation: 75.2, recommendedAllocation: 60 },
        { symbol: 'ETH', currentAllocation: 24.8, recommendedAllocation: 30 },
        { symbol: 'SOL', currentAllocation: 0, recommendedAllocation: 10 }
      ],
      performanceMetrics: {
        sharpeRatio: 1.8,
        maxDrawdown: -15.3,
        volatility: 45.2
      }
    }
    
    return {
      portfolio,
      analysis,
      timestamp: new Date()
    }
  }
}

// Export all tools as a collection
export const tradingTools: AITool[] = [
  getPortfolioTool,
  getWatchlistTool,
  getTechnicalAnalysisTool,
  addToWatchlistTool,
  removeFromWatchlistTool,
  getMarketDataTool,
  getOpenOrdersTool,
  cancelOrderTool,
  setSymbolTool,
  bulkBuyWatchlistTool,
  analyzePortfolioTool,
  ...visualDemoTools, // Add visual demo tools
  ...buySellTools,    // Add real trading tools with visual animations
  ...watchlistTools   // Add watchlist tools with visual interactions
] 